import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';

import { speakingRoomsApi, type SpeakingRoomInput } from '@/api/speakingRooms';
import { humanizeError } from '@/lib/errors';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Pill } from '@/components/ui/Pill';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { AudiencePicker, type AudienceValue } from '@/components/curator/AudiencePicker';
import { useConfirm } from '@/components/ui/ConfirmProvider';
import { Modal } from '@/components/ui/Modal';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useAuthStore } from '@/stores/auth';
import { ActionSheet } from '@/components/mobile/ActionSheet';
import { MAdd, MBlock, MEmpty, MRow, MTag, MTitle } from '@/components/mobile/MobileKit';

const LEVELS = ['', 'A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

function fmt(iso: string): string {
  return new Date(iso).toLocaleString('ru-RU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

// datetime-local default: now + 1h, rounded, in the input's expected format.
function defaultWhen(): string {
  const d = new Date(Date.now() + 3600_000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function CuratorSpeakingRoomsPage() {
  const { t } = useTranslation();
  const ask = useConfirm();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [err, setErr] = useState('');
  const [form, setForm] = useState({
    title: '', topic: '', level: '', when: defaultWhen(), duration: 60, capacity: 25,
    // По умолчанию — ГРУППЕ, а не всем.
    //
    // ⚠️ Это не мелочь оформления. Комната, открытая всем, показывается всей
    // школе: на потоке в 600 человек места разберут чужие ученики, а тем, для
    // кого её собирали, не достанется. Заказчик 01.09.2026 прямо просил
    // «исключительно по группам».
    audience: { audience_type: 'group' } as AudienceValue<'group' | 'marathon' | 'all'>,
  });

  const rooms = useQuery({ queryKey: ['curator', 'speaking-rooms'], queryFn: () => speakingRoomsApi.listMine() });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['curator', 'speaking-rooms'] });

  const createM = useMutation({
    mutationFn: () => {
      const input: SpeakingRoomInput = {
        title: form.title.trim(),
        topic: form.topic.trim() || undefined,
        level: form.level || undefined,
        scheduled_at: new Date(form.when).toISOString(),
        duration_min: form.duration,
        capacity: form.capacity,
        audience_type: form.audience.audience_type ?? 'group',
        marathon_id: form.audience.marathon_id ?? null,
        group_id: form.audience.group_id ?? null,
      };
      return speakingRoomsApi.create(input);
    },
    onSuccess: () => { setErr(''); setFormOpen(false); setForm({
    title: '', topic: '', level: '', when: defaultWhen(), duration: 60, capacity: 25,
    // По умолчанию — ГРУППЕ, а не всем.
    //
    // ⚠️ Это не мелочь оформления. Комната, открытая всем, показывается всей
    // школе: на потоке в 600 человек места разберут чужие ученики, а тем, для
    // кого её собирали, не достанется. Заказчик 01.09.2026 прямо просил
    // «исключительно по группам».
    audience: { audience_type: 'group' } as AudienceValue<'group' | 'marathon' | 'all'>,
  }); invalidate(); },
    onError: (e) => setErr(humanizeError(e)),
  });
  const cancelM = useMutation({
    mutationFn: (id: string) => speakingRoomsApi.cancel(id),
    onSuccess: () => { setErr(''); invalidate(); },
    onError: (e) => setErr(humanizeError(e)),
  });

  const items = rooms.data?.items ?? [];
  const isMobile = useIsMobile();
  // Куратору — только своя группа: «всем» и целому курсу комнату открывает администратор, сервер отвечает куратору
  // 403 (speaking-room-service, 16.10.2026). Страница общая для /curator и /admin, поэтому решает роль, а не адрес.
  const isCurator = useAuthStore((s) => s.user?.role) === 'curator';
  const roomTypes: readonly ('group' | 'marathon' | 'all')[] = isCurator ? ['group'] : ['group', 'marathon', 'all'];
  const [formOpen, setFormOpen] = useState(false);
  const [sheetFor, setSheetFor] = useState<(typeof items)[number] | null>(null);
  const askCancel = async (r: { id: string; title: string }) => {
    if (await ask({ message: t('curatorRooms.cancelAsk', { title: r.title }), danger: true })) cancelM.mutate(r.id);
  };
  const createButton = (
    <Button loading={createM.isPending} disabled={
        !form.title.trim() ||
        // ⚠️ Без адресата комната молча стала бы «для всех» — см. выше.
        (form.audience.audience_type === 'group' && !form.audience.group_id) ||
        (form.audience.audience_type === 'marathon' && !form.audience.marathon_id)
      } onClick={() => createM.mutate()}>
      <span className="material-symbols-outlined">add</span>
      {t('curatorRooms.create')}
    </Button>
  );

  // Поля «Новой комнаты» — одни на обе раскладки: на компьютере карточкой над списком, на телефоне окном во весь экран
  // по «+» (docs/CURATOR-MOBILE-DESIGN.md §4.12, правило 3).
  const fields = (
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Input label={t('cueSets.name')} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={t('curatorRooms.roomNameExample')} />
          </div>
          <div className="sm:col-span-2">
            <Input label={t('curatorRooms.topicOptional')} value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} />
          </div>
          <Input type="datetime-local" label={t('curatorRooms.when')} value={form.when} onChange={(e) => setForm({ ...form, when: e.target.value })} />
          <Select
            label={t('cueSets.level')}
            value={form.level}
            onChange={(e) => setForm({ ...form, level: e.target.value })}
            options={LEVELS.map((l) => ({ value: l, label: l || t('cueSets.anyLevel') }))}
          />
          <Input type="number" min={10} label={t('curatorRooms.duration')} value={String(form.duration)} onChange={(e) => setForm({ ...form, duration: Number(e.target.value) || 60 })} />
          <Input type="number" min={2} label={t('curatorRooms.capacity')} value={String(form.capacity)} onChange={(e) => setForm({ ...form, capacity: Number(e.target.value) || 25 })} />
          <div className="sm:col-span-2">
            <AudiencePicker
              labelPrefix="curatorTasks"
              types={roomTypes}
              value={form.audience}
              onChange={(patch) => setForm({ ...form, audience: { ...form.audience, ...patch } })}
            />
            <p className="mt-1 text-xs text-on-surface-variant">{t('speakingRooms.audienceHint')}</p>
            {isCurator && <p className="mt-1 text-xs text-on-surface-variant">{t('speakingRooms.curatorGroupOnly')}</p>}
          </div>
        </div>
  );

  // ── Телефон (docs/CURATOR-MOBILE-DESIGN.md §4.12) ─────────────────────────
  if (isMobile) {
    return (
      <div className="space-y-3">
        <MTitle
          title={t('speakingRooms.title')}
          subtitle={t('curatorRooms.subtitle')}
          right={<MAdd label={t('curatorRooms.newRoom')} onClick={() => setFormOpen(true)} />}
        />
        {err && !formOpen && <p className="px-0.5 text-[13px] text-error">{err}</p>}
        {rooms.isLoading ? (
          <p className="px-0.5 text-[13px] text-on-surface-variant">{t('common.loading')}</p>
        ) : items.length === 0 ? (
          <MEmpty icon="record_voice_over" title={t('curatorRooms.empty')} />
        ) : (
          <MBlock>
            {items.map((r, i) => {
              const cancelled = r.status === 'cancelled' || r.status === 'finished';
              return (
                <MRow
                  key={r.id}
                  wrap
                  dim={cancelled}
                  title={r.title}
                  meta={[fmt(r.scheduled_at), t('curatorRooms.minutes', { n: r.duration_min }), r.level].filter(Boolean).join(' · ')}
                  aside={
                    <span className="flex flex-none items-center gap-1">
                      <MTag tone={cancelled ? 'muted' : r.seats_taken >= r.capacity ? 'warm' : 'info'}>
                        {cancelled
                          ? t('curatorRooms.cancelled')
                          : t('curatorRooms.seatsTaken', { taken: r.seats_taken, capacity: r.capacity })}
                      </MTag>
                      {!cancelled && (
                        <button
                          type="button"
                          onClick={() => setSheetFor(r)}
                          aria-label={t('nav.more')}
                          className="grid h-11 w-11 place-items-center rounded-full text-on-surface-variant"
                        >
                          <span className="material-symbols-outlined text-[20px]">more_horiz</span>
                        </button>
                      )}
                    </span>
                  }
                  below={
                    cancelled ? undefined : (
                      <div className="pb-3">
                        <Button size="md" onClick={() => navigate(`/curator/speaking/${r.id}`)}>
                          <span className="material-symbols-outlined">login</span>
                          {t('curatorRooms.joinHost')}
                        </Button>
                      </div>
                    )
                  }
                  last={i === items.length - 1}
                />
              );
            })}
          </MBlock>
        )}
        <ActionSheet
          open={!!sheetFor}
          onClose={() => setSheetFor(null)}
          title={sheetFor?.title}
          actions={
            sheetFor
              ? [{ label: t('curatorRooms.cancel'), icon: 'cancel', danger: true, onSelect: () => askCancel(sheetFor) }]
              : []
          }
        />
        <Modal
          open={formOpen}
          onClose={() => setFormOpen(false)}
          title={t('curatorRooms.newRoom')}
          phoneFull
          footer={<div className="grid">{createButton}</div>}
        >
          {err && <p className="mb-2 text-[13px] text-error">{err}</p>}
          {fields}
        </Modal>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <PageHeader title={t('speakingRooms.title')} subtitle={t('curatorRooms.subtitle')} />
      {err && <Card variant="subtle" className="text-error">{err}</Card>}

      {/* Создать комнату */}
      <Card>
        <CardTitle>{t('curatorRooms.newRoom')}</CardTitle>
        {fields}
        <div className="mt-3">{createButton}</div>
      </Card>

      {/* Мои комнаты */}
      {rooms.isLoading ? (
        <Card variant="subtle">{t('common.loading')}</Card>
      ) : items.length === 0 ? (
        <Card variant="subtle" className="text-center text-on-surface-variant">{t('curatorRooms.empty')}</Card>
      ) : (
        <div className="space-y-3">
          {items.map((r) => {
            const cancelled = r.status === 'cancelled' || r.status === 'finished';
            return (
              <Card key={r.id} className={cancelled ? 'opacity-60' : ''}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-[180px] flex-1">
                    <CardTitle>{r.title}</CardTitle>
                    <p className="mt-0.5 text-sm text-on-surface-variant">{fmt(r.scheduled_at)} · {t('curatorRooms.minutes', { n: r.duration_min })}</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {r.level && <Pill variant="neutral">{r.level}</Pill>}
                      <Pill variant={r.seats_taken >= r.capacity ? 'warning' : 'success'}>{t('curatorRooms.seatsTaken', { taken: r.seats_taken, capacity: r.capacity })}</Pill>
                      {cancelled && <Pill variant="danger">{t('curatorRooms.cancelled')}</Pill>}
                    </div>
                  </div>
                  {!cancelled && (
                    <div className="flex flex-col items-end gap-2">
                      <Button onClick={() => navigate(`/curator/speaking/${r.id}`)}>
                        <span className="material-symbols-outlined">login</span>
                        {t('curatorRooms.joinHost')}
                      </Button>
                      <Button variant="ghost" size="sm" loading={cancelM.isPending} onClick={() => askCancel(r)}>
                        {t('curatorRooms.cancel')}
                      </Button>
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
